import {
  isUserPhaseResponsibleOrAbove,
  validateCreateMilestoneRequest,
  validateUpdateMilestoneRequest,
  validateUpdateApprovalRequest,
  validateCreateApprovalRequest,
  validateRemoveMilestoneRequest,
  validateRemoveApprovalRequest,
  isParentDone,
  isMilestoneChildInProgressOrDone,
  isMilestoneChildInProgress,
  validateRequestMilestoneApprovalRequest,
} from '@beeboard/common';
import {
  Project,
  MilestoneCreate,
  MilestoneUpdate,
  ApprovalCreate,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useMilestoneProjectStore } from './milestoneProjectStore';
import { useProfileStore } from './profileStore';
import { useProjectStore } from './projectStore';

export const useMilestoneProjectRulesStore = defineStore(
  'milestoneProjectRulesStore',
  {
    getters: {
      user() {
        return useProfileStore().profile;
      },

      project() {
        return (projectId: string): Project => {
          const project = useProjectStore().project(projectId);

          return project;
        };
      },

      projectMembers() {
        return (projectId: string) => {
          const project = useProjectStore().projectMembers(projectId);

          return project;
        };
      },

      milestone() {
        return (projectId: string, milestoneId: string) => {
          const milestone = useMilestoneProjectStore().milestone(
            projectId,
            milestoneId,
          );

          return milestone;
        };
      },

      milestoneSummary() {
        return (projectId: string, milestoneId: string) => {
          const milestone = useProjectStore().milestoneSummary(
            projectId,
            milestoneId,
          );

          return milestone;
        };
      },

      isIntermediateMilestone() {
        return (projectId: string, milestoneId: string) => {
          const isIntermediateMilestone =
            useMilestoneProjectStore().isIntermediateMilestone(
              projectId,
              milestoneId,
            );

          return isIntermediateMilestone;
        };
      },

      milestonePhase() {
        return (projectId: string, milestoneId: string) => {
          const phase = useProjectStore().milestonePhase(
            projectId,
            milestoneId,
          );

          return phase;
        };
      },
    },

    actions: {
      isPhaseResponsibleOrAboveForMilestone(
        projectId: string,
        milestoneId: string,
      ) {
        const project = this.project(projectId);

        return isUserPhaseResponsibleOrAbove(
          this.user.id,
          project,
          this.milestonePhase(projectId, milestoneId).id,
          project.editor_ids,
        );
      },

      isMilestoneActionsDisabled(projectId: string, milestoneId: string) {
        return (
          !this.isPhaseResponsibleOrAboveForMilestone(projectId, milestoneId) ||
          this.isMilestoneDone(projectId, milestoneId)
        );
      },

      isMilestoneDone(projectId: string, milestoneId: string) {
        return this.milestoneSummary(projectId, milestoneId).state === 'done';
      },

      validateCreateMilestoneRequest(
        projectId: string,
        milestonePayload: MilestoneCreate,
      ) {
        return validateCreateMilestoneRequest(
          this.project(projectId),
          milestonePayload,
        );
      },

      validateUpdateMilestoneRequest(
        projectId: string,
        milestoneId: string,
        milestonePayload: MilestoneUpdate,
      ) {
        return validateUpdateMilestoneRequest(
          this.project(projectId),
          milestonePayload,
          this.milestoneSummary(projectId, milestoneId),
          milestonePayload.phase ??
            this.milestonePhase(projectId, milestoneId).id,
          this.isIntermediateMilestone(projectId, milestoneId),
        );
      },

      validateUpdateApprovalRequest(
        projectId: string,
        milestoneId: string,
        approvalId: string,
      ) {
        return validateUpdateApprovalRequest(
          approvalId,
          this.milestone(projectId, milestoneId),
        );
      },

      validateCreateApprovalRequest(
        projectId: string,
        milestoneId: string,
        approvalPayload: ApprovalCreate,
      ) {
        return validateCreateApprovalRequest(
          this.project(projectId),
          approvalPayload,
          this.milestone(projectId, milestoneId),
          this.projectMembers(projectId),
        );
      },

      validateRemoveMilestoneRequest(projectId: string, milestoneId: string) {
        return validateRemoveMilestoneRequest(
          this.project(projectId),
          this.milestone(projectId, milestoneId),
          this.milestonePhase(projectId, milestoneId).id,
        );
      },

      validateRemoveApprovalRequest(
        projectId: string,
        milestoneId: string,
        approvalId: string,
      ) {
        return validateRemoveApprovalRequest(
          approvalId,
          this.milestone(projectId, milestoneId),
        );
      },

      validateRequestMilestoneApprovalRequest(
        projectId: string,
        approvalId: string,
        milestoneId: string,
      ) {
        return validateRequestMilestoneApprovalRequest(
          approvalId,
          this.milestone(projectId, milestoneId),
        );
      },

      isChildInProgressOrDone(projectId: string, milestoneId: string) {
        return isMilestoneChildInProgressOrDone(
          this.project(projectId),
          this.milestonePhase(projectId, milestoneId),
        );
      },

      isChildInProgress(projectId: string, milestoneId: string) {
        return isMilestoneChildInProgress(
          this.project(projectId),
          this.milestone(projectId, milestoneId),
          this.milestonePhase(projectId, milestoneId),
        );
      },

      // Cannot modify intermediate milestone if parent is done
      isParentDone(projectId: string, milestoneId: string) {
        return isParentDone(this.milestonePhase(projectId, milestoneId).state);
      },
    },
  },
);
